/* eslint-disable complexity */
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { OutlineButton } from 'apps/sigma2-ui/src/components/ui/Button';
import { ObservationWrapperInternal } from 'apps/sigma2-ui/src/state/observation';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useIsLoadingObservation } from 'apps/sigma2-ui/src/lib/hooks/useIsLoadingObservation';
import { MainLayout } from 'apps/sigma2-ui/src/components/MainLayout';
import { Helmet } from 'react-helmet';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { useParams } from 'react-router';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { DashboardChartRecordDto } from 'libs/end-to-end/src/dashboard';
import { DraftDashboardChart } from 'libs/end-to-end/src/dashboard';
import { ObservationLoadingAndFailureIndicators } from 'apps/sigma2-ui/src/components/ObservationLoadingAndFailureIndicators';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { ProgressCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/ProgressDefinition';
import { ProgressChartCard } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageInterface';
import { useAuthenticatedPageRedirect } from 'apps/sigma2-ui/src/lib/hooks/useAuthenticatedPageRedirect';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { ExploreSectionCard } from 'apps/sigma2-ui/src/explore-section/components/ExploreSectionInterface';
import { HeatmapAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/HeatmapAnalysisDefinition';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { SuccessPhenotypeV2CustomVizDefinition } from 'libs/end-to-end/src/custom-viz/SuccessPhenotypeDefinition';
import { DynamicPipelineAnalysisCard } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysisFullInterface';
import { PipelineAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/PipelineAnalysisDefinition';
import { BinScatterMultiSelectCard } from 'apps/sigma2-ui/src/components/bin-scatter/BinScatterMultiSelectFullInterface';
import { BinScatterMultiSelectCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/BinScatterMultiSelectDefinition';
import { FocusAreaNewDataIntelligenceSummaryOverviewCard } from 'apps/sigma2-ui/src/focus-area/FocusAreaNewDataIntelligenceSummaryInterface';
import { BiasAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/BiasAnalysisDefinition';
import { EditableName } from 'apps/sigma2-ui/src/components/dashboards/EditableName';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { MoreItemsIconSvg } from 'apps/sigma2-ui/src/components/svg/MoreItemsIcon';
import { Dropdown } from 'apps/sigma2-ui/src/components/Dropdown';
import { DropdownDivider } from 'apps/sigma2-ui/src/components/Dropdown';
import { IconTrashCanSvg } from 'apps/sigma2-ui/src/components/svg/IconTrashCan';
import { ExternalLinkSvg } from 'apps/sigma2-ui/src/components/svg/ExternalLinkSvg';
import { IconDuplicateOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconDuplicateOutline';
import { callGenerateShareLink } from 'apps/sigma2-ui/src/state/generate-share-link';
import { DropdownItemProps } from 'apps/sigma2-ui/src/components/Dropdown';
import { useReports } from 'apps/sigma2-ui/src/reports/hooks/useReports';
import { useIsUserAnalysisEnabled } from 'apps/sigma2-ui/src/components/ui/useUserAnalysis';
import { isEntitledToDashboards } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToReports } from 'libs/end-to-end/src/group-entitlements';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { DashboardFOBButton } from 'apps/sigma2-ui/src/components/DashboardFOBButton';
import { TableViewCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TableViewDefinition';
import { TableViewCard } from 'apps/sigma2-ui/src/explore-section/components/TableView';
import { EditDashboardChartSlideMenu } from 'apps/sigma2-ui/src/components/EditDashboardChartSlideMenu';
import { IconEditSvg } from 'apps/sigma2-ui/src/components/svg/IconEdit';
import { Setter } from 'libs/end-to-end/src/ui-types';
import useMeasure from 'react-use-measure';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getFeatureFromCustomVizId } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SummaryStatsCustomVizDefinition } from 'libs/end-to-end/src/summary-stats-definition';
import { SummaryStatsCard } from 'apps/sigma2-ui/src/explore-section/components/SummaryStats';
import { RichTextEditorCard } from 'apps/sigma2-ui/src/components/rich-text/RichTextEditor';
import { RichTextCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/RichTextDefinition';
import { DragDropProvider } from 'apps/sigma2-ui/src/pages/Dashboards/DragDropContext';
import { DraggableChartCard } from 'apps/sigma2-ui/src/pages/Dashboards/DraggableChartCard';
import { DragPreview } from 'apps/sigma2-ui/src/pages/Dashboards/DragPreview';
import { ProgressBarChartWithDatesCard } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressBarChartCard';
import { TimeSeriesTrendsCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsDefinition';
import { ImperativePanelGroupHandle } from 'react-resizable-panels';
import { Panel } from 'react-resizable-panels';
import { PanelGroup } from 'react-resizable-panels';
import { PanelResizeHandle } from 'react-resizable-panels';
import cn from 'classnames';
import { IconDragHandleSvg } from 'apps/sigma2-ui/src/components/svg/IconDragHandle';
import { GenerateReportRequest } from 'libs/end-to-end/src/generate-report';
import { GeolocationCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/GeolocationDefinition';
import { ExternalStreamlitCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/ExternalStreamlitDefinition';
import { ExternalStreamlitCard } from 'apps/sigma2-ui/src/components/external-streamlit/ExternalStreamlitCard';
import { ExternalStreamlitForPrototypeCard } from 'apps/sigma2-ui/src/components/external-streamlit/ExternalStreamlitCard';
import { VerticalResizeHandle } from 'apps/sigma2-ui/src/pages/Dashboards/VerticalResizeHandle';
import { KeyFactorsCard } from 'apps/sigma2-ui/src/pages/KeyFactorsPage';
import { GoogleMapComponent } from 'apps/sigma2-ui/src/components/GoogleMap';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import { isDashboardsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPublishDashboardsEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isShowDashboardAISummariesEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';
import { Editor } from '@tiptap/react';
import { RichTextOutput } from 'libs/end-to-end/src/custom-viz/RichTextTypes';
import { RichTextRequest } from 'libs/end-to-end/src/custom-viz/RichTextTypes';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';
import { ErrorBoundary } from 'apps/sigma2-ui/src/components/ErrorBoundary';
import { useBlocker } from 'react-router';
import { UnsavedChangesModal } from 'apps/sigma2-ui/src/components/dashboards/UnsavedChangesModal';
import { DashboardStaleModal } from 'apps/sigma2-ui/src/components/dashboards/DashboardStaleModal';
import { useEmbedMode } from 'apps/sigma2-ui/src/components/EmbedModeContext';
import { DashboardSelectDataSourceSlideMenu } from 'apps/sigma2-ui/src/components/dashboards/DashboardSelectDataSourceSlideMenu';
import { SettingsIconSvg } from 'apps/sigma2-ui/src/components/svg/SettingsIcon';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { CodeIconSvg } from 'apps/sigma2-ui/src/components/svg/CodeIcon';
import { DashboardAddGlobalFiltersSlideMenu } from 'apps/sigma2-ui/src/components/dashboards/DashboardAddGlobalFiltersSlideMenu';
import { useSearchParams } from 'react-router-dom';
import { areSearchParamsTheSame } from 'apps/sigma2-ui/src/state/url-utils';
import { useSelectedDashboardDatabaseRevisionId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDashboardDatabaseRevisionId';
import { useSelectedOrganizationId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedOrganizationId';
import { useSelectedDatabaseId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseId';
import { useSelectedDatabaseRevisionId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseRevisionId';
import { determineEffectiveDatabaseRevisionIdForDashboard } from 'libs/end-to-end/src/dashboards.utils';
import { normalizeChartVizRequestForGlobalFilters } from 'libs/end-to-end/src/dashboards.utils';
import { useUserObservationOrgAndDbIds } from 'apps/sigma2-ui/src/lib/hooks/useUserObservationOrgAndDbIds';
import { isEqual } from 'lodash';
import { useDashboardGlobalFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useGlobalFilters';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { getFeaturesForNewChart } from 'apps/sigma2-ui/src/lib/charts/get-features-for-new-chart';
import { CreateDashboardChartSlideMenu } from 'apps/sigma2-ui/src/components/CreateDashboardChartSlideMenu';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { AddFilterSvg } from 'apps/sigma2-ui/src/components/svg/AddFilterSvg';
import { UserAnalysisSignaling } from 'apps/sigma2-ui/src/state/user-analysis-signalling';
import { lookupCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { resolveFocusAreaForCustomViz } from 'libs/end-to-end/src/construct-body-display-of-custom-viz';
import { determinePossiblePrototypeStreamlitAppsForMyObservation } from 'libs/end-to-end/src/my-observation-utils';
import { DashboardDetailsHeader } from 'apps/sigma2-ui/src/components/dashboards/DashboardDetailsHeader';
import { DashboardAISummaryHeaderControl } from 'apps/sigma2-ui/src/components/dashboards/DashboardAISummaryHeaderControl';
import { ProsemirrorOutput } from 'libs/end-to-end/src/prosemirror';
import { isEntitledToAi } from 'libs/end-to-end/src/group-entitlements';
import { getPlainTextFromProsemirror } from 'apps/sigma2-ui/src/lib/custom-viz/rich-text';

const GeneratedByAISummaryLabelColor = '#52575B';
const GeneratedByAISummaryLabelClass = 'ai-summary-label';

const formatDashboardAISummaryDate = (createdAtEpoch: number) => {
  return new Date(createdAtEpoch).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const stripLeadingHeadingNodes = (
  summary: ProsemirrorOutput,
): ProsemirrorOutput => {
  const content = summary.content.slice();

  while (content.length > 1 && content[0]?.type === 'heading') {
    content.shift();
  }

  return {
    ...summary,
    content,
  };
};

const ADD_STATISTIC_IMG = '/images/dashboards/add-statistic.png';
const GRID_GAP = 12;
const GRID_COLUMNS = 10;

const DRAG_HANDLE_SIZE = { width: 14, height: 14 };

const buildBackgroundLines = () => {
  const numberOfColumns = 10;
  const backgroundImage = Array(numberOfColumns * 2)
    .fill('linear-gradient(to bottom, #ECECEC 2px, transparent 2px)')
    .join(', ');

  const backgroundPosition = Array(numberOfColumns + 1)
    .fill(null)
    .map((_, index) => {
      if (index === 0) {
        return '0px 0';
      }
      if (index === numberOfColumns) {
        return '100% 0';
      }
      return `calc(${index * numberOfColumns}% - ${GRID_GAP / 2}px), calc(${index * numberOfColumns}% + ${GRID_GAP / 2}px) 0`;
    })
    .join(', ');

  return {
    backgroundImage,
    backgroundSize: '1px 6px',
    backgroundRepeat: 'repeat-y',
    backgroundPosition,
    minHeight: 'calc(100vh - 4rem)',
  };
};

export const getCardWidth = (containerWidth: number, tempCardWidth: number) => {
  if (!containerWidth) {
    return Math.round(tempCardWidth);
  }

  const columnWidth = containerWidth / GRID_COLUMNS;
  const tempCardWidthInPx = (tempCardWidth * containerWidth) / 100;
  const capCardToColumnWidth = Math.round(tempCardWidthInPx / columnWidth);
  const widthPercentage =
    ((capCardToColumnWidth * columnWidth) / containerWidth) * 100;

  return Math.round(widthPercentage);
};

export const HeaderBar = ({
  onSaveAndCloseGlobalFilters,
  isAISummaryDisabled = false,
}: {
  onSaveAndCloseGlobalFilters: (
    newGlobalFilters: FilterDefinitionForExtrasArray | null,
  ) => void;
  isAISummaryDisabled?: boolean;
}) => {
  const language = useLanguage();
  const { myObservation } = useObservation();
  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();
  const {
    addDraftChart,
    persistDashboardDraft,
    persistDashboardDraftState,
    reorderDraftCharts,
    updateDashboardDraftName,
    updateDraftChart,
    dashboardDraft,
  } = useDashboards();

  const onAfterSave = useCallback(
    async (name: string) => {
      if (name === dashboardDraft?.draft.name) return;

      updateDashboardDraftName(name);
    },
    [dashboardDraft?.draft.name, updateDashboardDraftName],
  );

  const { exportReport } = useReports();
  const isEmbedMode = useEmbedMode();
  const [isExporting, setIsExporting] = useState(false);

  const charts = useMemo(
    () => dashboardDraft?.draft.charts ?? [],
    [dashboardDraft?.draft.charts],
  );

  const generateReportRequest: GenerateReportRequest | null = useMemo(() => {
    if (
      !dashboardDraft ||
      !selectedDatabaseRevisionId ||
      !selectedOrganizationId ||
      !selectedDatabaseId
    ) {
      return null;
    }

    return {
      charts: charts.map((chart) => ({
        customVizRequest: normalizeChartVizRequestForGlobalFilters({
          customVizRequest: chart.customVizRequest,
          databaseId: selectedDatabaseId,
          organizationId: selectedOrganizationId,
          databaseRevisionId: selectedDatabaseRevisionId,
          filterSetDefinitionsWithValues:
            myObservation?.filterSetDefinitionsWithValues ?? [],
          globalFilters: dashboardDraft?.draft.globalFilters ?? [],
          focusAreas: myObservation?.focusAreas ?? [],
          pipelineAnalysisConfiguration:
            myObservation?.pipelineAnalysisConfiguration,
        }),
        name: chart.name,
        description: chart.description ?? undefined,
      })),
      title: dashboardDraft?.draft.name,
    };
  }, [
    charts,
    dashboardDraft,
    myObservation?.filterSetDefinitionsWithValues,
    myObservation?.focusAreas,
    myObservation?.pipelineAnalysisConfiguration,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
  ]);

  const onExport = useCallback(
    (onComplete?: () => void) => {
      if (!generateReportRequest) {
        return;
      }

      setIsExporting(true);

      exportReport(generateReportRequest, () => {
        setTimeout(() => {
          setIsExporting(false);
          onComplete?.();
        }, 3000);
      });
    },
    [generateReportRequest, exportReport],
  );

  const isUserAnalysisEnabled = useIsUserAnalysisEnabled();
  const showDownloadMultiAnalysisButton = useMemo(
    () =>
      Boolean(myObservation?.showDownloadMultiAnalysisButton) &&
      isUserAnalysisEnabled &&
      isEntitledToReports({
        features: myObservation?.features ?? [],
      }),
    [
      myObservation?.showDownloadMultiAnalysisButton,
      myObservation?.features,
      isUserAnalysisEnabled,
    ],
  );

  const showPublishDashboardButton = useMemo(
    () =>
      isPublishDashboardsEnabledForRevision({ revisionFlags: myObservation }),
    [myObservation],
  );

  const showDashboardAISummaryControl = useMemo(
    () =>
      isShowDashboardAISummariesEnabledForRevision({
        revisionFlags: myObservation,
      }) &&
      isEntitledToAi({
        features: myObservation?.features ?? [],
      }),
    [myObservation],
  );

  const handlePersistDashboardChanges = useCallback(async () => {
    await persistDashboardDraft();
  }, [persistDashboardDraft]);

  const handleSummaryReady = useCallback(
    ({
      summary,
      createdAtEpoch,
    }: {
      summary: ProsemirrorOutput;
      createdAtEpoch: number;
    }) => {
      if (
        !dashboardDraft ||
        !selectedOrganizationId ||
        !selectedDatabaseId ||
        !selectedDatabaseRevisionId
      ) {
        return;
      }

      const topPositionOnDashboard =
        charts.length === 0
          ? 0
          : Math.min(...charts.map((chart) => chart.positionOnDashboard));

      const shiftedCharts = charts.map((chart) =>
        chart.positionOnDashboard >= topPositionOnDashboard
          ? {
              ...chart,
              positionOnDashboard: chart.positionOnDashboard + 1,
            }
          : chart,
      );

      shiftedCharts.forEach(updateDraftChart);

      const summaryBody = stripLeadingHeadingNodes(summary);
      const summaryTitle = language.dashboardSummaryStampedTitle({
        dashboardName: dashboardDraft.draft.name,
        date: formatDashboardAISummaryDate(createdAtEpoch),
      });

      const summaryWithGeneratedByLabel: ProsemirrorOutput = {
        type: 'doc',
        content: [
          {
            type: 'paragraph',
            attrs: {
              class: GeneratedByAISummaryLabelClass,
            },
            content: [
              {
                type: 'text',
                text: 'Generated by AI',
                marks: [
                  {
                    type: 'textStyle',
                    attrs: {
                      color: GeneratedByAISummaryLabelColor,
                    },
                  },
                ],
              },
            ],
          },
          {
            type: 'heading',
            attrs: {
              level: 1,
            },
            content: [
              {
                type: 'text',
                text: summaryTitle,
                marks: [],
              },
            ],
          },
          ...summaryBody.content,
        ],
      };

      const summaryChart = addDraftChart({
        dashboardId: dashboardDraft.dashboardId,
        customVizRequest: {
          customVizId: 'richText',
          organizationId: selectedOrganizationId,
          observationLocationType: {
            type: 'db-revision',
            databaseId: selectedDatabaseId,
            databaseRevisionId: selectedDatabaseRevisionId,
          },
          inputs: {
            dropdowns: {},
            extras: {
              richText: {
                serializedContent: JSON.stringify(summaryWithGeneratedByLabel),
                plainText: getPlainTextFromProsemirror(
                  summaryWithGeneratedByLabel,
                ),
              },
            },
          },
        },
        name: 'AI Summary',
        description: '',
        positionOnDashboard: topPositionOnDashboard,
        positionOnRow: 1,
        width: 100,
        height: null,
        defaultViewMode: 'table',
        selectedMetric: null,
        baseColors: null,
        viewConfiguration: {},
      });

      reorderDraftCharts([
        summaryChart.draftId,
        ...shiftedCharts.map((chart) => chart.draftId),
      ]);
    },
    [
      addDraftChart,
      charts,
      dashboardDraft,
      reorderDraftCharts,
      selectedDatabaseId,
      selectedDatabaseRevisionId,
      selectedOrganizationId,
      language,
      updateDraftChart,
    ],
  );

  const [isSelectDataSourceSlideMenuOpen, setIsSelectDataSourceSlideMenuOpen] =
    useState(false);

  return (
    <DashboardDetailsHeader
      dashboardDraft={dashboardDraft}
      isEmbedMode={isEmbedMode}
      onAfterSave={onAfterSave}
      showPublishDashboardButton={showPublishDashboardButton}
      onSave={handlePersistDashboardChanges}
      savePending={persistDashboardDraftState.state === 'pending'}
      showDownloadMultiAnalysisButton={showDownloadMultiAnalysisButton}
      onExport={onExport}
      exportPending={isExporting}
      summaryControl={
        showDashboardAISummaryControl ? (
          <DashboardAISummaryHeaderControl
            key={
              isAISummaryDisabled ? 'ai-summary-disabled' : 'ai-summary-enabled'
            }
            disabled={isAISummaryDisabled}
            organizationId={selectedOrganizationId}
            databaseId={selectedDatabaseId}
            databaseRevisionId={selectedDatabaseRevisionId}
            dashboardId={dashboardDraft?.dashboardId ?? null}
            generateReportRequest={generateReportRequest}
            onSummaryReady={handleSummaryReady}
          />
        ) : null
      }
      menu={
        <DashboardDropdown
          openDashboardDropdown={isSelectDataSourceSlideMenuOpen}
          setOpenDashboardDropdown={setIsSelectDataSourceSlideMenuOpen}
          onSaveAndCloseGlobalFilters={onSaveAndCloseGlobalFilters}
        />
      }
    />
  );
};

const NavigateToButton = ({
  text,
  feature,
  setShowNewChartMenu,
  setSelectedNewChartFeature,
}: {
  text: string;
  feature: FiltersFeatureName;
  setShowNewChartMenu: Setter<boolean>;
  setSelectedNewChartFeature: Setter<FiltersFeatureName | null>;
}) => {
  const handleClick = useCallback(
    () => setSelectedNewChartFeature(feature),
    [feature, setSelectedNewChartFeature],
  );

  return (
    <OutlineButton
      className="pb-2 px-1"
      behavior={{ type: 'onClick', onClick: handleClick }}
    >
      {text}
    </OutlineButton>
  );
};

export const AddStatisticCard = ({
  setShowNewChartMenu,
  setSelectedNewChartFeature,
}: {
  setShowNewChartMenu: Setter<boolean>;
  setSelectedNewChartFeature: Setter<FiltersFeatureName | null>;
}) => {
  const { myObservation } = useObservation();
  const language = useLanguage();

  const enabledFeatures = useMemo(
    () => (myObservation ? getFeaturesForNewChart(myObservation) : []),
    [myObservation],
  );

  const handleAddChartClick = useCallback(
    () => setShowNewChartMenu(true),
    [setShowNewChartMenu],
  );

  return (
    <div
      data-testid="empty-dashboard-placeholder"
      className="rounded-md border-5 border-1 border-solid border-gray-150 bg-white col-span-5 w-fit max-w-[637px]"
    >
      <Spacing direction="vertical" size="lg" className="pb-6">
        <TouchTarget
          content={() => (
            <div className="bg-[#F5F8FF] py-20 items-center justify-items-center">
              <img src={ADD_STATISTIC_IMG} />
            </div>
          )}
          behavior={{
            type: 'onClick',
            onClick: handleAddChartClick,
          }}
        />
        <Spacing direction="vertical" size="md" className="items-center mt-1">
          <Spacing direction="vertical" size="xs" className="items-center">
            <S2Typography
              textColorVariant="royal-900"
              variant="headline-5-alt"
              dangerouslyOverrideClassNameFn={(cn) =>
                `${cn.replace('font-bold', 'font-medium')}`
              }
              translatedContent={language.addAStatistic()}
            />
            <S2Typography
              variant="body-6"
              translatedContent={language.addStatisticLegend()}
            />
          </Spacing>
          <div className="flex flex-wrap justify-center px-20">
            {enabledFeatures.includes('timeSeriesTrendsPrimary') && (
              <NavigateToButton
                text={language.outcomeTrends()}
                feature={'timeSeriesTrendsPrimary'}
                setSelectedNewChartFeature={setSelectedNewChartFeature}
                setShowNewChartMenu={setShowNewChartMenu}
              />
            )}
            {enabledFeatures.includes('progressPage') && (
              <NavigateToButton
                text={language.disparityTrends()}
                feature={'progressPage'}
                setSelectedNewChartFeature={setSelectedNewChartFeature}
                setShowNewChartMenu={setShowNewChartMenu}
              />
            )}
            {enabledFeatures.includes('binScatterMultiSelect') && (
              <NavigateToButton
                text={language.groupTrends()}
                feature={'binScatterMultiSelect'}
                setSelectedNewChartFeature={setSelectedNewChartFeature}
                setShowNewChartMenu={setShowNewChartMenu}
              />
            )}
            {enabledFeatures.includes('exploreHeatmap') && (
              <NavigateToButton
                text={language.controlledDisparities()}
                feature={'exploreHeatmap'}
                setSelectedNewChartFeature={setSelectedNewChartFeature}
                setShowNewChartMenu={setShowNewChartMenu}
              />
            )}
            {enabledFeatures.includes('successFactors') && (
              <NavigateToButton
                text={language.keyFactorsPageTitle()}
                feature={'successFactors'}
                setSelectedNewChartFeature={setSelectedNewChartFeature}
                setShowNewChartMenu={setShowNewChartMenu}
              />
            )}
            {enabledFeatures.includes('summaryStats') && (
              <NavigateToButton
                text={language.stat()}
                feature={'summaryStats'}
                setSelectedNewChartFeature={setSelectedNewChartFeature}
                setShowNewChartMenu={setShowNewChartMenu}
              />
            )}
            {enabledFeatures.includes('tableView') && (
              <NavigateToButton
                text={language.dataTable()}
                feature={'tableView'}
                setSelectedNewChartFeature={setSelectedNewChartFeature}
                setShowNewChartMenu={setShowNewChartMenu}
              />
            )}
          </div>
        </Spacing>
      </Spacing>
    </div>
  );
};

interface ComponentProps {
  customVizRequest: CustomVizRequest | undefined;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  chart?: DraftDashboardChart;
}

const ChartCardContent = ({
  chart,
  normalizedChartCustomVizRequest,
  component,
  componentProps,
}: {
  chart: DraftDashboardChart;
  normalizedChartCustomVizRequest: CustomVizRequest;
  component: React.FC<ComponentProps>;
  componentProps?: Record<string, any>;
}) => {
  const context = useCustomViz();
  const { doViz } = context;

  const { myObservation } = useObservation();
  const { updateDraftChart } = useDashboards();

  const vizRef = useRef<CustomVizRequest | null>(null);
  useEffect(
    () => {
      if (
        !vizRef.current ||
        !isEqual(vizRef.current, normalizedChartCustomVizRequest)
      ) {
        vizRef.current = normalizedChartCustomVizRequest;
        doViz(normalizedChartCustomVizRequest);
      }
    },
    // do not add doViz as an effect dependency to avoid unnecessary renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [normalizedChartCustomVizRequest],
  );

  // Base props that all components receive
  const baseProps = useMemo(() => {
    return {
      customVizRequest: normalizedChartCustomVizRequest,
      result: context.result,
      status: context.status,
    };
  }, [normalizedChartCustomVizRequest, context.result, context.status]);

  const setViewConfiguration = useCallback(
    (viewConfiguration: ViewConfigurationSchema) => {
      const updatedChart: DraftDashboardChart = {
        ...chart,
        viewConfiguration: {
          ...chart.viewConfiguration,
          ...viewConfiguration,
        },
      };
      updateDraftChart(updatedChart);
    },
    [chart, updateDraftChart],
  );

  const possiblePrototypeStreamlitApps = useMemo(() => {
    return determinePossiblePrototypeStreamlitAppsForMyObservation({
      customVizId: normalizedChartCustomVizRequest.customVizId,
      myObservation,
    });
  }, [normalizedChartCustomVizRequest, myObservation]);

  const prototypeStreamlitAppId = useMemo(() => {
    const current = chart.viewConfiguration.streamlitPrototypeAppId ?? null;
    return current !== null &&
      possiblePrototypeStreamlitApps.some((app) => app.id === current)
      ? current
      : null;
  }, [
    chart.viewConfiguration.streamlitPrototypeAppId,
    possiblePrototypeStreamlitApps,
  ]);

  const prototypeComponent: React.FC<ComponentProps> | null = useMemo(() => {
    if (prototypeStreamlitAppId) {
      return ExternalStreamlitForPrototypeCard;
    }
    return null;
  }, [prototypeStreamlitAppId]);

  // Add chart data for TableViewCard to enable database persistence
  const props = useMemo(() => {
    // TODO: This is kind of temporary because we had an old mechanism for configuring the heatmap that required passing in the filter definition through the request instead of the view configuration.
    if (normalizedChartCustomVizRequest.customVizId === 'heatmap') {
      return {
        ...baseProps,
        chart: chart,
        viewConfiguration: {
          selectedFilterDefinition:
            normalizedChartCustomVizRequest.inputs.extras.filterDefinition,
          ...chart.viewConfiguration,
        },
        setViewConfiguration,
      };
    }

    return {
      ...baseProps,
      chart: chart,
      viewConfiguration: chart.viewConfiguration,
      setViewConfiguration,
    };
  }, [baseProps, chart, normalizedChartCustomVizRequest, setViewConfiguration]);

  return React.createElement(
    prototypeComponent ? prototypeComponent : component,
    { ...componentProps, ...props },
  );
};

const ChartDropdown = ({
  chart,
  normalizedChartCustomVizRequest,
  openChartDropdown,
  openMoveToForChart,
  setOpenChartDropdown,
  setOpenMoveToForChart,
}: {
  chart: DraftDashboardChart;
  normalizedChartCustomVizRequest: CustomVizRequest;
  openChartDropdown: string | undefined;
  openMoveToForChart: string | undefined;
  setOpenChartDropdown: Setter<string | undefined>;
  setOpenMoveToForChart: Setter<string | undefined>;
}) => {
  const [isLoading, setIsLoading] = useState<
    'deleting' | 'editing' | 'duplicating' | 'moving' | null
  >(null);

  const {
    deleteDraftChart,
    addDraftChart,
    updateDraftChart,
    dashboardsList,
    moveDashboardChart,
    dashboardDraft,
  } = useDashboards();

  const { userAuthenticatedApiCall } = useUser();

  const [linkToChart, setLinkToChart] = useState<string>('');

  const isDropdownOpen = openChartDropdown === chart.draftId;

  const setIsDropdownOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setOpenChartDropdown(chart.draftId);
      } else if (isDropdownOpen) {
        setOpenChartDropdown(undefined);
        setOpenMoveToForChart(undefined);
      }
    },
    [
      chart.draftId,
      isDropdownOpen,
      setOpenChartDropdown,
      setOpenMoveToForChart,
    ],
  );

  const isMoveToDropdownOpen = openMoveToForChart === chart.draftId;

  const setIsMoveToDropdownOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setOpenMoveToForChart(chart.draftId);
      } else if (isMoveToDropdownOpen) {
        setOpenMoveToForChart(undefined);
      }
    },
    [chart.draftId, isMoveToDropdownOpen, setOpenMoveToForChart],
  );

  const chartCustomVizSupportsShareLink = useMemo(() => {
    const vizDef = lookupCustomVizDefinition(
      normalizedChartCustomVizRequest.customVizId,
    );

    const focusArea =
      resolveFocusAreaForCustomViz(
        normalizedChartCustomVizRequest,
      )?.toLocaleLowerCase() || null;

    const url =
      vizDef.getUrlPath?.(focusArea, normalizedChartCustomVizRequest) ?? null;
    return url !== null;
  }, [normalizedChartCustomVizRequest]);

  const shareVizRef = useRef<CustomVizRequest | null>(null);
  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (
        isAlive() &&
        chartCustomVizSupportsShareLink &&
        (!shareVizRef.current ||
          !isEqual(shareVizRef.current, normalizedChartCustomVizRequest))
      ) {
        shareVizRef.current = normalizedChartCustomVizRequest;
        const shareLinkResult = await callGenerateShareLink({
          windowUrl: window.location.origin,
          customVizRequest: normalizedChartCustomVizRequest,
          userAuthenticatedApiCall,
        });

        if (!shareLinkResult) return;

        setLinkToChart(shareLinkResult.shareLink);
      }
    },
    [
      chartCustomVizSupportsShareLink,
      normalizedChartCustomVizRequest,
      userAuthenticatedApiCall,
    ],
  );

  const handleDeleteChart = useCallback(() => {
    deleteDraftChart(chart.draftId);
  }, [chart.draftId, deleteDraftChart]);

  const handleDuplicateChart = useCallback(() => {
    const targetRow = chart.positionOnDashboard + 1;

    addDraftChart({
      ...chart,
      id: undefined,
      name: `${chart.name} (Copy)`,
      positionOnDashboard: targetRow,
      positionOnRow: 1,
      width: 100, // Full width for new row
    });
  }, [chart, addDraftChart]);

  const handleMoveToDashboard = useCallback(
    async (targetDashboardId: string) => {
      setIsLoading('moving');

      uiAppLogger.info('Moving chart to dashboard:', {
        chartId: chart.id,
        targetDashboardId,
      });

      if (!chart.id) {
        return;
      }

      await moveDashboardChart({
        sourceDashboardChartId: chart.id,
        sourceDashboardId: chart.dashboardId,
        targetDashboardId,
      });

      setIsLoading(null);
    },
    [chart, moveDashboardChart],
  );

  const moveToItems = useMemo<DropdownItemProps[]>(() => {
    return (
      dashboardsList
        ?.filter((dashboard) => dashboard.id !== chart.dashboardId)
        .map((dashboard) => ({
          key: dashboard.id,
          content: (
            <div className="flex gap-2 items-center min-w-[250px]">
              {dashboard.name}
            </div>
          ),
          behavior: chart.id
            ? {
                type: 'onClick',
                onClick: () => handleMoveToDashboard(dashboard.id),
              }
            : undefined,
        })) ?? []
    );
  }, [dashboardsList, chart.dashboardId, chart.id, handleMoveToDashboard]);

  const dropdownItems = useMemo<DropdownItemProps[]>(
    () => [
      {
        key: 'Edit',
        content: (
          <div className="flex gap-2 items-center">
            {isLoading === 'editing' ? (
              <Spinner size={17} textColor="text-gray-900" />
            ) : (
              <IconEditSvg
                svg={{ width: 17, height: 17 }}
                wantsCurrentColor={true}
              />
            )}

            <div>Edit</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: () => setIsLoading('editing'),
        },
      },
      {
        key: 'divider#1',
        content: <DropdownDivider />,
      },
      ...(chartCustomVizSupportsShareLink
        ? [
            {
              key: 'Go to original chart',
              content: (
                <div className="flex gap-2 items-center">
                  <ExternalLinkSvg
                    svg={{ width: 17, height: 17 }}
                    wantsCurrentColor={true}
                  />
                  <div className="whitespace-nowrap">Go to original chart</div>
                </div>
              ),
              behavior: {
                type: 'href' as const,
                href: linkToChart,
              },
            },
          ]
        : []),
      {
        key: 'Duplicate chart',
        content: (
          <div className="flex gap-2 items-center">
            {isLoading === 'duplicating' ? (
              <Spinner size={17} textColor="text-gray-900" />
            ) : (
              <IconDuplicateOutlineSvg
                svg={{ width: 17, height: 17 }}
                wantsCurrentColor={true}
              />
            )}
            <div>Duplicate chart</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: handleDuplicateChart,
        },
      },
      {
        key: 'MoveTo',
        content: (
          <Dropdown
            trigger={
              <div className="flex gap-2 items-center -mx-4 -my-2 ">
                {isLoading === 'moving' ? (
                  <Spinner size={17} textColor="text-gray-900" />
                ) : (
                  <IconChevronRightSvg svg={{ width: 18, height: 18 }} />
                )}
                <div>Move to</div>
              </div>
            }
            items={moveToItems}
            disabled={isLoading === 'moving' || moveToItems.length === 0}
            isOpen={isMoveToDropdownOpen}
            setIsOpen={setIsMoveToDropdownOpen}
          />
        ),
      },
      {
        key: 'divider#2',
        content: <DropdownDivider />,
      },
      {
        key: 'Remove',
        content: (
          <div className="flex gap-2 items-center text-red-700">
            {isLoading === 'deleting' ? (
              <Spinner size={17} textColor="text-red-700" />
            ) : (
              <IconTrashCanSvg svg={{ width: 17, height: 17 }} />
            )}

            <div>Remove</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: handleDeleteChart,
        },
      },
    ],
    [
      isLoading,
      chartCustomVizSupportsShareLink,
      linkToChart,
      handleDuplicateChart,
      moveToItems,
      isMoveToDropdownOpen,
      setIsMoveToDropdownOpen,
      handleDeleteChart,
    ],
  );

  const saveChart = useCallback(
    (dashboardChart: Partial<DashboardChartRecordDto>) => {
      updateDraftChart({
        ...chart,
        ...dashboardChart,
      });
      return { ...chart, ...dashboardChart };
    },
    [chart, updateDraftChart],
  );

  const feature = getFeatureFromCustomVizId({
    customVizId: normalizedChartCustomVizRequest.customVizId,
  });

  if (isLoading === 'editing' && feature && dashboardDraft) {
    return (
      <EditDashboardChartSlideMenu
        isOpen={true}
        setIsOpen={() => setIsLoading(null)}
        feature={feature}
        saveChart={saveChart}
        dashboardChart={chart}
        dashboardId={chart.dashboardId}
        dashboard={dashboardDraft.draft}
      />
    );
  }

  return (
    <div data-testid="chart-dropdown">
      <Dropdown
        trigger={<MoreItemsIconSvg />}
        items={dropdownItems}
        isOpen={isDropdownOpen}
        setIsOpen={setIsDropdownOpen}
      />
    </div>
  );
};

const ChartHeader = ({
  chart,
  dragHandleRef,
}: {
  chart: DraftDashboardChart;
  dragHandleRef: React.RefObject<HTMLDivElement>;
}) => {
  const { updateDraftChart } = useDashboards();
  const isEmbedMode = useEmbedMode();

  const handleUpdateDashboardChart = useCallback(
    (newChartData: { name?: string; description?: string }) => {
      const { name, description } = newChartData;

      if ((name && name !== chart.name) || description !== chart.description) {
        updateDraftChart({
          ...chart,
          ...newChartData,
        });
      }
    },
    [chart, updateDraftChart],
  );

  const onAfterSaveName = useCallback(
    (name: string) => {
      handleUpdateDashboardChart({ name });
    },
    [handleUpdateDashboardChart],
  );

  const onAfterSaveDescription = useCallback(
    (description: string) => {
      handleUpdateDashboardChart({ description });
    },
    [handleUpdateDashboardChart],
  );

  const stopPropagation = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={cn(`flex-1 flex flex-col p-4 group`, {
        'cursor-grab active:cursor-grabbing': !isEmbedMode,
      })}
      ref={dragHandleRef}
    >
      <div className="px-3 flex relative">
        {!isEmbedMode && (
          <div
            className="absolute  top-3.5 -left-1 opacity-0 group-hover:opacity-100 transition-opacity duration-100 text-gray-600"
            onMouseDown={stopPropagation}
          >
            <IconDragHandleSvg
              svg={DRAG_HANDLE_SIZE}
              wantsCurrentColor={true}
            />
          </div>
        )}

        <div className="flex items-center w-full chart-header">
          <div className="w-full">
            <EditableName
              name={chart.name}
              size="headline-4"
              onAfterSave={onAfterSaveName}
            />
          </div>
        </div>
      </div>
      {chart.description && (
        <div className="px-3 py-2 chart-description">
          <EditableName
            name={chart.description}
            size="body-6"
            onAfterSave={onAfterSaveDescription}
          />
        </div>
      )}

      <div className="flex px-3 @2xl:hidden">
        <CardDataRefreshIndicator />
      </div>
    </div>
  );
};

const RichTextChartWrapper = ({
  chart,
  normalizedChartCustomVizRequest,
  dragHandleRef,
  openChartDropdown,
  setOpenChartDropdown,
  openMoveToForChart,
  setOpenMoveToForChart,
}: {
  chart: DraftDashboardChart;
  normalizedChartCustomVizRequest: CustomVizRequest;
  dragHandleRef: React.RefObject<HTMLDivElement>;
  openChartDropdown: string | undefined;
  setOpenChartDropdown: Setter<string | undefined>;
  openMoveToForChart: string | undefined;
  setOpenMoveToForChart: Setter<string | undefined>;
}) => {
  const dragHandleWrapperRef = useRef<HTMLDivElement>(null);
  const language = useLanguage();
  const isEmbedMode = useEmbedMode();

  const [isHovered, setIsHovered] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [content, setContent] = useState<{
    serializedContent: string;
    plainText: string;
  } | null>(null);

  const { updateDraftChart } = useDashboards();

  const handleSetIsEditable = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setIsEditable(true);
    },
    [],
  );

  const handleFinishEditing = useCallback(() => {
    if (
      !isEditable ||
      chart.customVizRequest.customVizId !== 'richText' ||
      !content
    ) {
      return;
    }

    setIsSaving(true);

    const customVizRequest: RichTextRequest = {
      ...chart.customVizRequest,
      inputs: {
        ...chart.customVizRequest.inputs,
        extras: {
          richText: content,
        },
      },
    };

    updateDraftChart({
      ...chart,
      customVizRequest,
    });

    setIsDirty(false);
    setIsEditable(false);
    setIsSaving(false);
  }, [isEditable, chart, content, updateDraftChart]);

  const handleApplyContent = useCallback((editor: Editor) => {
    setIsDirty(true);
    setContent({
      serializedContent: JSON.stringify(editor.getJSON()),
      plainText: editor.getText() ?? '',
    });
  }, []);

  useEffect(() => {
    if (content || chart.customVizRequest.customVizId !== 'richText') return;

    const newContent: RichTextOutput = {
      serializedContent:
        chart.customVizRequest.inputs.extras.richText.serializedContent,
      plainText: chart.customVizRequest.inputs.extras.richText.plainText,
    };

    setContent(newContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const result = useMemo<CustomVizHookResult | null>(() => {
    if (!content) return null;

    return {
      resultType: 'ok',
      value: {
        serializedContent: content?.serializedContent,
        plainText: content?.plainText ?? '',
      },
    };
  }, [content]);

  return (
    <ClickAwayListener
      onClickAway={handleFinishEditing}
      touchEvent="onTouchStart"
      mouseEvent="onMouseDown"
    >
      <div
        className="relative h-full group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleSetIsEditable}
      >
        {/* Floating controls - only visible on hover */}
        <div
          className={cn(
            'absolute right-2 z-30 transition-opacity duration-200',
            isHovered || isEditable ? 'opacity-100' : 'opacity-0',
          )}
          ref={dragHandleRef}
        >
          {!isEmbedMode && (
            <div className="flex gap-2 items-center bg-white/90 backdrop-blur-sm rounded-lg shadow-md p-1 border border-gray-200">
              <div
                ref={dragHandleWrapperRef}
                className={
                  'px-2 text-gray-600 cursor-grab active:cursor-grabbing'
                }
              >
                <IconDragHandleSvg
                  svg={{ width: 14, height: 14 }}
                  wantsCurrentColor={true}
                />
              </div>
              {isDirty && (
                <Button
                  variant="outline"
                  innerClassName={[
                    'border-0 !ring-0 !ring-offset-0 dropdown-button text-gray-600 !px-2 ',
                  ]}
                  className="!w-full min-w-[47px]"
                  size="small"
                  role="button"
                  behavior={{
                    type: 'onClick',
                    onClick: handleFinishEditing,
                  }}
                >
                  {isSaving ? (
                    <Spinner size={17} textColor="text-gray-900" />
                  ) : (
                    language.save()
                  )}
                </Button>
              )}
              <ChartDropdown
                chart={chart}
                openChartDropdown={openChartDropdown}
                setOpenChartDropdown={setOpenChartDropdown}
                openMoveToForChart={openMoveToForChart}
                setOpenMoveToForChart={setOpenMoveToForChart}
                normalizedChartCustomVizRequest={
                  normalizedChartCustomVizRequest
                }
              />
            </div>
          )}
        </div>

        {/* Content with max-width constraint - gradient handled in CSS - text is selectable */}
        <div className="h-full overflow-hidden relative select-text">
          {result && (
            <div
              className={cn('pl-[20px] h-full', {
                'pt-[44px]': !isEditable,
              })}
            >
              <RichTextEditorCard
                result={result}
                status="waiting-for-user"
                isEditable={isEditable && !isEmbedMode}
                onApply={handleApplyContent}
              />
            </div>
          )}
        </div>
      </div>
    </ClickAwayListener>
  );
};

const CardDataRefreshIndicator = () => {
  const hook = useCustomViz();
  return (
    <>
      <DataRefreshIndicator
        status={hook.status}
        resultCreatedAt={hook.resultCreatedAt}
        heightClass={'h-auto'}
      />
    </>
  );
};

const DASHBOARD_LOADING_STATUSES = new Set<CustomVizHookStatus>([
  'initialization',
  'loading-dropdowns',
  'loading-viz',
]);

const ChartCardStatusObserver = ({
  chartId,
  onStatusChange,
}: {
  chartId: string;
  onStatusChange: (chartId: string, status: CustomVizHookStatus) => void;
}) => {
  const hook = useCustomViz();

  useEffect(() => {
    onStatusChange(chartId, hook.status);
  }, [chartId, hook.status, onStatusChange]);

  return null;
};

const ChartCard = ({
  chart,
  normalizedChartCustomVizRequest,
  containerYScrollRef,
  openChartDropdown,
  setOpenChartDropdown,
  openMoveToForChart,
  setOpenMoveToForChart,
  onCustomVizStatusChange,
}: {
  chart: DraftDashboardChart;
  normalizedChartCustomVizRequest: CustomVizRequest;
  containerYScrollRef: React.RefObject<HTMLDivElement>;
  openChartDropdown: string | undefined;
  setOpenChartDropdown: Setter<string | undefined>;
  openMoveToForChart: string | undefined;
  setOpenMoveToForChart: Setter<string | undefined>;
  onCustomVizStatusChange: (
    chartId: string,
    status: CustomVizHookStatus,
  ) => void;
}) => {
  const { userAuthenticatedApiCall } = useUser();

  const { myObservation, selectedOrganizationId } = useObservation();
  const isEmbedMode = useEmbedMode();

  const chartProps = useMemo(() => {
    switch (chart.customVizRequest.customVizId) {
      case 'progress':
        return {
          component: ProgressChartCard,
          vizDefinition: ProgressCustomVizDefinition,
        };
      case 'heatmap':
        return {
          component: ExploreSectionCard,
          vizDefinition: HeatmapAnalysisCustomVizDefinition,
        };
      case 'successPhenotypeV2':
        return {
          component: KeyFactorsCard,
          vizDefinition: SuccessPhenotypeV2CustomVizDefinition,
        };
      case 'pipelineAnalysis':
        return {
          component: DynamicPipelineAnalysisCard,
          vizDefinition: PipelineAnalysisCustomVizDefinition,
        };
      case 'binScatterMultiSelect':
        return {
          component: BinScatterMultiSelectCard,
          vizDefinition: BinScatterMultiSelectCustomVizDefinition,
        };
      case 'biasAnalysis':
        return {
          component: FocusAreaNewDataIntelligenceSummaryOverviewCard,
          vizDefinition: BiasAnalysisCustomVizDefinition,
        };
      case 'tableView':
        return {
          component: TableViewCard,
          vizDefinition: TableViewCustomVizDefinition,
        };
      case 'summaryStats':
        return {
          component: SummaryStatsCard,
          vizDefinition: SummaryStatsCustomVizDefinition,
        };
      case 'richText':
        return {
          component: RichTextEditorCard,
          vizDefinition: RichTextCustomVizDefinition,
        };
      case 'timeSeriesTrends':
        return {
          component: ProgressBarChartWithDatesCard,
          vizDefinition: TimeSeriesTrendsCustomVizDefinition,
        };
      case 'geolocation':
        return {
          component: GoogleMapComponent,
          vizDefinition: GeolocationCustomVizDefinition,
        };
      case 'externalStreamlit':
        return {
          component: ExternalStreamlitCard,
          vizDefinition: ExternalStreamlitCustomVizDefinition,
        };
    }
  }, [chart.customVizRequest.customVizId]);

  const dragHandleRef = useRef<HTMLDivElement>(null);

  const isRichText = chart.customVizRequest.customVizId === 'richText';

  if (!myObservation || !selectedOrganizationId || !chartProps) {
    return null;
  }

  return (
    <DraggableChartCard
      chart={chart}
      dragHandleRef={dragHandleRef}
      containerYScrollRef={containerYScrollRef}
    >
      {isRichText ? (
        // Rich Text: Minimal wrapper, content-focused with floating controls on hover
        <CustomViz
          observation={myObservation.type}
          organizationId={selectedOrganizationId}
          customVizDefinition={chartProps.vizDefinition}
          userAuthenticatedApiCall={userAuthenticatedApiCall}
        >
          <ChartCardStatusObserver
            chartId={chart.draftId}
            onStatusChange={onCustomVizStatusChange}
          />
          <RichTextChartWrapper
            chart={chart}
            normalizedChartCustomVizRequest={normalizedChartCustomVizRequest}
            dragHandleRef={dragHandleRef}
            openChartDropdown={openChartDropdown}
            setOpenChartDropdown={setOpenChartDropdown}
            openMoveToForChart={openMoveToForChart}
            setOpenMoveToForChart={setOpenMoveToForChart}
          />
        </CustomViz>
      ) : (
        // Standard charts: Full card chrome with header
        <Card paddingClassName="p-0 border-solid border-gray-200 border-1 relative h-full flex flex-col">
          <CustomViz
            observation={myObservation.type}
            organizationId={selectedOrganizationId}
            customVizDefinition={chartProps.vizDefinition}
            userAuthenticatedApiCall={userAuthenticatedApiCall}
          >
            <ChartCardStatusObserver
              chartId={chart.draftId}
              onStatusChange={onCustomVizStatusChange}
            />
            <div className="sticky top-0 z-20 border-b-1 border-gray-200 bg-white rounded-t-lg @container">
              <div className={'flex flow-row w-full'}>
                <ChartHeader chart={chart} dragHandleRef={dragHandleRef} />
                <div
                  className={cn('flex flex-row h-[40px] pt-2', {
                    'pr-1': !isEmbedMode,
                    'pr-4': isEmbedMode,
                  })}
                >
                  <div className="hidden @2xl:flex">
                    <CardDataRefreshIndicator />
                  </div>
                  {!isEmbedMode && (
                    <ChartDropdown
                      chart={chart}
                      normalizedChartCustomVizRequest={
                        normalizedChartCustomVizRequest
                      }
                      openChartDropdown={openChartDropdown}
                      setOpenChartDropdown={setOpenChartDropdown}
                      openMoveToForChart={openMoveToForChart}
                      setOpenMoveToForChart={setOpenMoveToForChart}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex overflow-x-auto @container flex-grow group/dashboard">
              <ErrorBoundary>
                <ChartCardContent
                  chart={chart}
                  normalizedChartCustomVizRequest={
                    normalizedChartCustomVizRequest
                  }
                  component={chartProps.component}
                />
              </ErrorBoundary>
            </div>
          </CustomViz>
        </Card>
      )}
    </DraggableChartCard>
  );
};

export const DashboardDetailsPage = () => {
  const yScrollRef = useRef<HTMLDivElement>(null);
  const lastLoadedDashboardId = useRef<string | null>(null);

  return (
    <div className="bg-[#FDFDFF]">
      <MainLayout showBackToTop={false} scrollableRef={yScrollRef}>
        <DashboardDetailsPageObservationWrapper>
          <DashboardDetailsPagePartsWhichDependOnObservation
            lastLoadedDashboardId={lastLoadedDashboardId}
          />
        </DashboardDetailsPageObservationWrapper>
      </MainLayout>
    </div>
  );
};

export const DashboardDetailsPageObservationWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const selectedDashboardDatabaseRevisionId =
    useSelectedDashboardDatabaseRevisionId();
  const selectedOrganizationId = useSelectedOrganizationId();
  const selectedDatabaseId = useSelectedDatabaseId();
  const selectedDatabaseRevisionId = useSelectedDatabaseRevisionId();

  const { myUser } = useUser();

  const effectiveDatabaseRevisionId = useMemo(() => {
    return determineEffectiveDatabaseRevisionIdForDashboard({
      dashboardDatabaseRevisionId: selectedDashboardDatabaseRevisionId,
      databaseId: selectedDatabaseId,
      myUser,
    });
  }, [myUser, selectedDashboardDatabaseRevisionId, selectedDatabaseId]);

  const component = useMemo(() => {
    // OPTIMIZATION: Our app is already using the one we care about, let's not create an extra context layer.
    if (
      !effectiveDatabaseRevisionId ||
      selectedDatabaseRevisionId === effectiveDatabaseRevisionId
    ) {
      return children;
    }

    // Provide both an observation wrapper and a user analysis signaling wrapper which allows toasts to track with the pinned revision inside the dashboard.
    return (
      <ObservationWrapperInternal
        selectedOrganization={selectedOrganizationId}
        selectedDatabase={selectedDatabaseId}
        selectedDatabaseRevision={effectiveDatabaseRevisionId}
      >
        <UserAnalysisSignaling>{children}</UserAnalysisSignaling>
      </ObservationWrapperInternal>
    );
  }, [
    children,
    effectiveDatabaseRevisionId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
    selectedOrganizationId,
  ]);

  return <>{component}</>;
};

// This gets placed in a wrapper which has a hi-jacked observation context.
const DashboardDetailsPagePartsWhichDependOnObservation = ({
  lastLoadedDashboardId,
}: {
  lastLoadedDashboardId: React.MutableRefObject<string | null>;
}) => {
  const { myObservation } = useObservation();
  const language = useLanguage();
  const { dashboardId } = useParams<{
    dashboardId: string;
  }>();
  useAuthenticatedPageRedirect();

  const { isLoadingObservation: isLoadingStill, failedToLoadObservation } =
    useIsLoadingObservation({
      isDashboardDetailPage: true,
    });

  const {
    fetchDashboardDetails,
    initDashboardDraft,
    dashboardDraft,
    isDashboardStale,
    acknowledgeDashboardStale,
  } = useDashboards();

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const { myUser } = useUser();
  const selectedDatabaseId = useSelectedDatabaseId();

  const draftDatabaseRevisionId = useMemo(() => {
    if (!dashboardDraft) {
      return null;
    }
    return determineEffectiveDatabaseRevisionIdForDashboard({
      dashboardDatabaseRevisionId: dashboardDraft.draft.databaseRevisionId,
      databaseId: selectedDatabaseId,
      myUser,
    });
  }, [dashboardDraft, myUser, selectedDatabaseId]);

  useDashboardDetailsPageQueryStringRedirect({
    draftDatabaseRevisionId,
  });

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dashboardDraft?.dirty === true &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowUnsavedModal(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dashboardDraft?.dirty) return;

      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dashboardDraft?.dirty]);

  const handleReload = useCallback(async () => {
    acknowledgeDashboardStale();
    if (dashboardId) {
      initDashboardDraft(null);
      const fresh = await fetchDashboardDetails(dashboardId);
      initDashboardDraft(fresh);
      if (fresh) {
        initDashboardDraft(fresh);
      }
    }
  }, [
    acknowledgeDashboardStale,
    dashboardId,
    fetchDashboardDetails,
    initDashboardDraft,
  ]);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive: IsAliveFn) => {
      // We need to wait on myObservation because fetchDashboardDetails depends on useUserObservationOrgAndDbIds.
      if (
        isAlive() &&
        myObservation &&
        dashboardId &&
        dashboardId !== lastLoadedDashboardId.current
      ) {
        lastLoadedDashboardId.current = dashboardId;
        initDashboardDraft(null);
        const dashboardsDetailsResponse =
          await fetchDashboardDetails(dashboardId);

        if (!dashboardsDetailsResponse) return;
        if (!isAlive()) return;
        initDashboardDraft(dashboardsDetailsResponse);
      }
    },
    [
      myObservation,
      dashboardId,
      fetchDashboardDetails,
      initDashboardDraft,
      lastLoadedDashboardId,
    ],
  );

  const isPageStillLoading = useMemo(
    () => isLoadingStill || dashboardDraft === null,
    [isLoadingStill, dashboardDraft],
  );

  const displayFOBButton = myObservation?.showFobButtonInDashboardPage;
  const [showNewChartMenu, setShowNewChartMenu] = useState(false);
  const [selectedNewChartFeature, setSelectedNewChartFeature] =
    useState<FiltersFeatureName | null>(null);

  return (
    <>
      <Helmet>
        <title>
          {documentTitle(getTranslatedContentAsString(language.dashboards()))}
        </title>
      </Helmet>

      <UserAnalysesOverlay onUserAnalysisCancel={() => {}}>
        {dashboardDraft && !isPageStillLoading && displayFOBButton && (
          <DashboardFOBButton setShowNewChartMenu={setShowNewChartMenu} />
        )}
      </UserAnalysesOverlay>
      <ObservationLoadingAndFailureIndicators
        isLoadingStill={isPageStillLoading}
        failedToLoadObservation={failedToLoadObservation}
        isDashboardDetailPage={true}
      />

      <UnsavedChangesModal
        show={showUnsavedModal}
        onCancel={() => {
          if (blocker.state === 'blocked') {
            blocker.reset();
          }
          setShowUnsavedModal(false);
        }}
        onConfirmLeave={() => {
          if (blocker.state === 'blocked') {
            blocker.proceed();
          }
          setShowUnsavedModal(false);
        }}
      />
      <DashboardStaleModal show={isDashboardStale} onConfirm={handleReload} />

      <CreateDashboardChartSlideMenu
        isOpen={showNewChartMenu}
        setIsOpen={setShowNewChartMenu}
        selectedFeature={selectedNewChartFeature}
        setSelectedFeature={setSelectedNewChartFeature}
      />
      {!isPageStillLoading && (
        <DashboardDetailsView
          setShowNewChartMenu={setShowNewChartMenu}
          setSelectedNewChartFeature={setSelectedNewChartFeature}
        />
      )}
    </>
  );
};

const DashboardDropdown = ({
  openDashboardDropdown,
  setOpenDashboardDropdown,
  onSaveAndCloseGlobalFilters,
}: {
  openDashboardDropdown: boolean;
  setOpenDashboardDropdown: Setter<boolean>;
  onSaveAndCloseGlobalFilters: (
    newGlobalFilters: FilterDefinitionForExtrasArray | null,
  ) => void;
}) => {
  const [isLoading, setIsLoading] = useState<
    | 'deleting'
    | 'settingDataSource'
    | 'duplicating'
    | 'settingGlobalFilters'
    | null
  >(null);

  const { dashboardDraft, deleteDashboard, duplicateDashboard } =
    useDashboards();
  const language = useLanguage();

  const navigate = useNavigateSafe();

  const computedIsOpen = useMemo(() => {
    if (isLoading === 'deleting' || isLoading === 'duplicating') {
      return true;
    }

    return openDashboardDropdown;
  }, [isLoading, openDashboardDropdown]);

  useEffect(() => {
    if (isLoading === 'duplicating') {
      (async () => {
        const duplicated = await duplicateDashboard({
          dashboardId: dashboardDraft!.dashboardId,
        });

        setIsLoading(null);

        if (duplicated) {
          setOpenDashboardDropdown(false); // close after success
          navigate(`/dashboards/${duplicated.id}`);
        }
      })();
    }

    if (isLoading === 'deleting') {
      (async () => {
        await deleteDashboard({
          dashboardId: dashboardDraft!.dashboardId,
        });

        setIsLoading(null);
        setOpenDashboardDropdown(false);
        navigate('/dashboards');
      })();
    }
  }, [
    isLoading,
    dashboardDraft,
    duplicateDashboard,
    deleteDashboard,
    navigate,
    setOpenDashboardDropdown,
  ]);

  const dropdownItems = useMemo<DropdownItemProps[]>(
    () => [
      {
        key: 'SetDataSource',
        content: (
          <div className="flex gap-2 items-center">
            {isLoading === 'settingDataSource' ? (
              <Spinner size={17} textColor="text-gray-900" />
            ) : (
              <CodeIconSvg
                svg={{ width: 17, height: 17 }}
                wantsCurrentColor={true}
              />
            )}

            <div>Data Source</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: () => setIsLoading('settingDataSource'),
        },
      },
      {
        key: 'divider#1',
        content: <DropdownDivider />,
      },
      {
        key: 'AddGlobalFilter',
        content: (
          <div className="flex gap-2 items-center">
            {isLoading === 'settingGlobalFilters' ? (
              <Spinner size={17} textColor="text-gray-900" />
            ) : (
              <AddFilterSvg wantsCurrentColor={true} />
            )}

            <div>Add Global Filter</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: () => setIsLoading('settingGlobalFilters'),
        },
      },
      {
        key: 'divider#2',
        content: <DropdownDivider />,
      },
      {
        key: 'DuplicateDashboard',
        content: (
          <div className="flex gap-2 items-center">
            {isLoading === 'duplicating' ? (
              <Spinner size={17} textColor="text-gray-900" />
            ) : (
              <IconDuplicateOutlineSvg
                svg={{ width: 17, height: 17 }}
                wantsCurrentColor={true}
              />
            )}
            <div>Duplicate dashboard</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: () => setIsLoading('duplicating'),
        },
      },
      {
        key: 'divider#3',
        content: <DropdownDivider />,
      },
      {
        key: 'Remove',
        content: (
          <div className="flex gap-2 items-center text-red-700">
            {isLoading === 'deleting' ? (
              <Spinner size={17} textColor="text-red-700" />
            ) : (
              <IconTrashCanSvg svg={{ width: 17, height: 17 }} />
            )}

            <div>Remove</div>
          </div>
        ),
        behavior: {
          type: 'onClick',
          onClick: () => setIsLoading('deleting'),
        },
      },
    ],
    [isLoading],
  );

  if (isLoading === 'settingDataSource' && dashboardDraft) {
    return (
      <DashboardSelectDataSourceSlideMenu
        isOpen={true}
        setIsOpen={() => setIsLoading(null)}
      />
    );
  }

  if (isLoading === 'settingGlobalFilters' && dashboardDraft) {
    return (
      <DashboardAddGlobalFiltersSlideMenu
        isOpen={true}
        setIsOpen={() => setIsLoading(null)}
        onSaveAndClose={onSaveAndCloseGlobalFilters}
      />
    );
  }

  return (
    <Dropdown
      innerClassName="border-0 !ring-0 !ring-offset-0 dropdown-button px-0 py-0"
      trigger={
        <div
          className={cn('flex items-center gap-1 text-royal-800', {
            'text-royal-700': openDashboardDropdown,
          })}
        >
          <SettingsIconSvg svg={{ className: 'w-6 h-6' }} wantsCurrentColor />
          <S2Typography
            translatedContent={language.settings()}
            variant="headline-6-alt"
            textColorVariant="inherit"
            dangerouslyOverrideClassNameFn={(className) =>
              `${className} hidden min-[1700px]:inline`
            }
          />
        </div>
      }
      items={dropdownItems}
      isOpen={computedIsOpen}
      setIsOpen={setOpenDashboardDropdown}
    />
  );
};

export const DashboardDetailsView = ({
  setShowNewChartMenu,
  setSelectedNewChartFeature,
}: {
  setShowNewChartMenu: Setter<boolean>;
  setSelectedNewChartFeature: Setter<FiltersFeatureName | null>;
}) => {
  const yScrollRef = useRef<HTMLDivElement>(null);

  const { myObservation } = useObservation();

  const { isLoadingObservation: isLoadingStill } = useIsLoadingObservation({
    isDashboardDetailPage: true,
  });

  const {
    selectedOrganizationId,
    selectedDatabaseId,
    selectedDatabaseRevisionId,
  } = useUserObservationOrgAndDbIds();

  const dashboardsEnabled = useMemo(() => {
    return (
      isDashboardsEnabledForRevision({ revisionFlags: myObservation }) &&
      isEntitledToDashboards({
        features: myObservation?.features ?? [],
      })
    );
  }, [myObservation]);

  const {
    updateDraftChart,
    reorderDraftCharts,
    dashboardDraft,
    updateDashboardDraftGlobalFilters,
  } = useDashboards();

  const [measureRef, measurements] = useMeasure();

  const dashboardId = useMemo(() => {
    return dashboardDraft?.dashboardId || null;
  }, [dashboardDraft?.dashboardId]);

  const globalFiltersHook = useDashboardGlobalFilters({
    dashboardId: dashboardId || '',
    updateDashboardDraftGlobalFilters,
    dashboard: dashboardDraft?.draft ?? null,
  });

  const initFromDraft = useCallback(
    (newGlobalFilters: FilterDefinitionForExtrasArray | null) => {
      const newValue =
        newGlobalFilters?.map((f) => ({
          secondaryFilterKey: f.columnHeaderValue,
          value: f.values,
        })) ?? [];

      globalFiltersHook.setFilters({
        globalFilterSelections: newValue,
        shouldPreventAutoSubmit: true,
      });
    },
    [globalFiltersHook],
  );

  const normalizeWidths = useCallback(
    (sizes: number[]) => {
      const sizesWithMin = sizes.map((size) => (size <= 30 ? 30 : size));
      const totalWidth = sizesWithMin.reduce((acc, size) => acc + size, 0);
      const cappedSizes = sizesWithMin.map((size) => (size / totalWidth) * 100);

      return cappedSizes.map((size) => getCardWidth(measurements.width, size));
    },
    [measurements.width],
  );

  const charts = useMemo(
    () => dashboardDraft?.draft.charts ?? [],
    [dashboardDraft?.draft.charts],
  );

  const handleMoveChart = useCallback(
    async (
      draggedChartId: string,
      targetChartId: string,
      position: 'top' | 'right' | 'bottom' | 'left',
    ) => {
      const draggedChart = charts.find((c) => c.draftId === draggedChartId);
      const targetChart = charts.find((c) => c.draftId === targetChartId);

      if (!draggedChart || !targetChart) return;

      // Remove dragged chart
      const remainingCharts = charts.filter(
        (c) => c.draftId !== draggedChartId,
      );

      const draggedIndex = charts.findIndex(
        (c) => c.draftId === draggedChartId,
      );
      const targetIndex = charts.findIndex((c) => c.draftId === targetChartId);

      const adjustedTargetIndex =
        draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;

      const insertIndex =
        position === 'top' || position === 'left'
          ? adjustedTargetIndex
          : adjustedTargetIndex + 1;

      let nextCharts: DraftDashboardChart[] = [];

      if (position === 'left' || position === 'right') {
        const targetRow = targetChart.positionOnDashboard;
        const isMovingFromDifferentRow =
          draggedChart.positionOnDashboard !== targetRow;

        const insertedCharts = [
          ...remainingCharts.slice(0, insertIndex),
          {
            ...draggedChart,
            positionOnDashboard: targetRow,
          },
          ...remainingCharts.slice(insertIndex),
        ];

        const chartsOnSameRow = insertedCharts.filter(
          (c) => c.positionOnDashboard === targetRow,
        );

        const widthOfEachChart = getCardWidth(
          measurements.width,
          (GRID_COLUMNS / chartsOnSameRow.length / GRID_COLUMNS) * 100,
        );

        const approximateWidths = chartsOnSameRow.map((_, index) =>
          index === chartsOnSameRow.length - 1
            ? 100 - widthOfEachChart * (chartsOnSameRow.length - 1)
            : widthOfEachChart,
        );

        const normalizedWidths = normalizeWidths(approximateWidths);

        nextCharts = insertedCharts.map((chart) => {
          if (chart.positionOnDashboard !== targetRow) {
            return chart;
          }

          const index = chartsOnSameRow.findIndex(
            (c) => c.draftId === chart.draftId,
          );

          return {
            ...chart,
            positionOnRow: index + 1,
            width:
              isMovingFromDifferentRow && index >= 0
                ? normalizedWidths[index]
                : chart.width,
          };
        });
      } else {
        const targetRow = targetChart.positionOnDashboard;
        const newRow = position === 'top' ? targetRow : targetRow + 1;

        const shiftedCharts = remainingCharts.map((chart) =>
          chart.positionOnDashboard >= newRow
            ? {
                ...chart,
                positionOnDashboard: chart.positionOnDashboard + 1,
              }
            : chart,
        );

        nextCharts = [
          ...shiftedCharts.slice(0, insertIndex),
          {
            ...draggedChart,
            positionOnDashboard: newRow,
            positionOnRow: 1,
            width: 100,
          },
          ...shiftedCharts.slice(insertIndex),
        ];
      }

      // Normalize row + column ordering
      const sortedCharts = [...nextCharts].sort((a, b) => {
        if (a.positionOnDashboard !== b.positionOnDashboard) {
          return a.positionOnDashboard - b.positionOnDashboard;
        }
        return (a.positionOnRow ?? 1) - (b.positionOnRow ?? 1);
      });

      const chartsByRow = new Map<number, DraftDashboardChart[]>();

      sortedCharts.forEach((chart) => {
        const row = chart.positionOnDashboard;
        const list = chartsByRow.get(row) ?? [];
        chartsByRow.set(row, [...list, chart]);
      });

      const finalCharts = Array.from(chartsByRow.entries())
        .sort(([a], [b]) => a - b)
        .flatMap(([rowIndex, rowCharts], rowIdx) =>
          rowCharts.map((chart, chartIdx) => ({
            ...chart,
            positionOnDashboard: rowIdx + 1,
            positionOnRow: chartIdx + 1,
            width: rowCharts.length === 1 ? 100 : chart.width,
          })),
        );

      finalCharts.forEach(updateDraftChart);
      reorderDraftCharts(finalCharts.map((c) => c.draftId));
    },
    [
      charts,
      measurements.width,
      normalizeWidths,
      reorderDraftCharts,
      updateDraftChart,
    ],
  );

  const backgroundStyle = useMemo(buildBackgroundLines, []);

  const chartRows = useMemo(() => {
    const rows = Array.from(
      new Set(charts.map((chart) => chart.positionOnDashboard)),
    );

    return rows.map((row) => ({
      row,
      charts: charts.filter((chart) => chart.positionOnDashboard === row),
    }));
  }, [charts]);

  const [isResizingRow, setIsResizingRow] = useState(-1);

  const panelGroupRefs = useRef<
    Record<number, ImperativePanelGroupHandle | null>
  >({});

  // handle resize of a row
  const handleOnLayout = useCallback(
    (sizes: number[], row: number) => {
      if (isResizingRow === -1) return;

      const normalizedSizes = normalizeWidths(sizes);

      let index = 0;

      charts
        .filter((chart) => chart.positionOnDashboard === row)
        .forEach((chart) => {
          updateDraftChart({
            ...chart,
            width: normalizedSizes[index++],
          });
        });
    },
    [charts, isResizingRow, normalizeWidths, updateDraftChart],
  );

  // handle stop resizing of a row
  const handleOnDragging = useCallback(
    (isDragging: boolean, row: number) => {
      if (isDragging && isResizingRow === -1) {
        setIsResizingRow(row);
        return;
      }

      if (!isDragging && isResizingRow !== -1) {
        const normalizedSizes = charts
          .filter((chart) => chart.positionOnDashboard === row)
          .map((chart) => chart.width);

        panelGroupRefs.current[row]?.setLayout(normalizedSizes);

        setIsResizingRow(-1);

        // Persist resize into draft
        charts
          .filter((chart) => chart.positionOnDashboard === row)
          .forEach((chart) => {
            updateDraftChart(chart);
          });

        reorderDraftCharts(charts.map((c) => c.draftId));
      }
    },
    [charts, isResizingRow, reorderDraftCharts, updateDraftChart],
  );

  const handleOnStopResizeVertically = useCallback(
    (height: number, row: number, chartId: string) => {
      const newCharts = charts.map((chart) =>
        chart.draftId === chartId ? { ...chart, height } : chart,
      );

      // Update draft with new height
      newCharts.forEach((chart) => {
        updateDraftChart(chart);
      });

      reorderDraftCharts(newCharts.map((c) => c.draftId));
    },
    [charts, reorderDraftCharts, updateDraftChart],
  );

  const isPageStillLoading = isLoadingStill || dashboardDraft === null;

  const [openChartDropdown, setOpenChartDropdown] = useState<string>();
  const [openMoveToForChart, setOpenMoveToForChart] = useState<
    string | undefined
  >();
  const [chartStatuses, setChartStatuses] = useState<
    Record<string, CustomVizHookStatus>
  >({});

  useEffect(() => {
    const currentChartIds = new Set(charts.map((chart) => chart.draftId));

    setChartStatuses((existingStatuses) => {
      const nextStatuses = Object.fromEntries(
        Object.entries(existingStatuses).filter(([chartId]) =>
          currentChartIds.has(chartId),
        ),
      );

      const existingChartIds = Object.keys(existingStatuses);
      const nextChartIds = Object.keys(nextStatuses);
      const didStatusKeysChange =
        existingChartIds.length !== nextChartIds.length ||
        existingChartIds.some((chartId) => !currentChartIds.has(chartId));

      return didStatusKeysChange ? nextStatuses : existingStatuses;
    });
  }, [charts]);

  const handleChartStatusChange = useCallback(
    (chartId: string, status: CustomVizHookStatus) => {
      setChartStatuses((existingStatuses) => {
        if (existingStatuses[chartId] === status) {
          return existingStatuses;
        }

        return {
          ...existingStatuses,
          [chartId]: status,
        };
      });
    },
    [],
  );

  const isDashboardContentLoading = useMemo(() => {
    return charts.some((chart) => {
      const status = chartStatuses[chart.draftId];

      return !status || DASHBOARD_LOADING_STATUSES.has(status);
    });
  }, [chartStatuses, charts]);

  return (
    dashboardsEnabled && (
      <>
        {dashboardDraft &&
          !isPageStillLoading &&
          selectedOrganizationId &&
          selectedDatabaseId &&
          selectedDatabaseRevisionId && (
            <DragDropProvider onMoveChart={handleMoveChart}>
              <HeaderBar
                onSaveAndCloseGlobalFilters={initFromDraft}
                isAISummaryDisabled={isDashboardContentLoading}
              />
              {dashboardDraft.draft.globalFilters && (
                <div className={'mt-4 lg:mx-16 md:mx-10 mx-4'}>
                  <HorizontalFilterBar items={globalFiltersHook.items} />
                </div>
              )}

              <div className="h-full lg:mx-16 md:mx-10 mx-4 mb-[8rem]">
                <div
                  ref={measureRef}
                  className="mx-3 pt-11 gap-3 w-full bg-cover"
                  style={{
                    ...backgroundStyle,
                  }}
                >
                  {charts.length === 0 && (
                    <AddStatisticCard
                      setShowNewChartMenu={setShowNewChartMenu}
                      setSelectedNewChartFeature={setSelectedNewChartFeature}
                    />
                  )}
                  {/*
                    these negative margins and matching paddings are a very dirty cheap trick to evade
                    PanelGroup's overflow hidden issues. When dragging charts we need overflow to be visible
                    in order to see the drop indicator.
                    */}
                  <div className="-m-3">
                    {chartRows.map(({ row, charts }) => (
                      <PanelGroup
                        key={row}
                        direction="horizontal"
                        ref={(el) => {
                          panelGroupRefs.current[row] = el;
                        }}
                        onLayout={(sizes) => handleOnLayout(sizes, row)}
                        className="p-3 py-2.5 flex flex-grow"
                      >
                        {charts.map((chart, index) => {
                          const normalizedChartCustomVizRequest =
                            normalizeChartVizRequestForGlobalFilters({
                              customVizRequest: chart.customVizRequest,
                              databaseId: selectedDatabaseId,
                              pipelineAnalysisConfiguration:
                                myObservation?.pipelineAnalysisConfiguration,
                              focusAreas: myObservation?.focusAreas ?? [],
                              globalFilters:
                                dashboardDraft.draft.globalFilters ?? [],
                              organizationId: selectedOrganizationId,
                              databaseRevisionId: selectedDatabaseRevisionId,
                              filterSetDefinitionsWithValues:
                                myObservation?.filterSetDefinitionsWithValues ??
                                [],
                            });
                          return (
                            <React.Fragment
                              key={`${chart.draftId}-${chart.positionOnRow}`}
                            >
                              <Panel
                                defaultSize={chart.width ?? 100}
                                minSize={27}
                                className={`-m-3 vertical-resize-handle-${row} flex flex-col`}
                              >
                                <VerticalResizeHandle
                                  key={row}
                                  row={row}
                                  chartId={chart.draftId}
                                  onStopResize={handleOnStopResizeVertically}
                                  initialHeight={chart.height ?? undefined}
                                >
                                  <div className="px-3 py-2.5 h-full">
                                    <ChartCard
                                      chart={chart}
                                      normalizedChartCustomVizRequest={
                                        normalizedChartCustomVizRequest
                                      }
                                      containerYScrollRef={yScrollRef}
                                      openChartDropdown={openChartDropdown}
                                      setOpenChartDropdown={
                                        setOpenChartDropdown
                                      }
                                      openMoveToForChart={openMoveToForChart}
                                      setOpenMoveToForChart={
                                        setOpenMoveToForChart
                                      }
                                      onCustomVizStatusChange={
                                        handleChartStatusChange
                                      }
                                    />
                                  </div>
                                </VerticalResizeHandle>
                              </Panel>
                              {index < charts.length - 1 && (
                                <div
                                  key={`${chart.draftId}-resize-handle`}
                                  className={cn(
                                    'flex opacity-0 hover:opacity-100 transition-opacity duration-100 z-10',
                                    {
                                      'opacity-100': isResizingRow === row,
                                    },
                                  )}
                                >
                                  <PanelResizeHandle
                                    className="w-1 rounded-md bg-blue-500 mx-1  my-2.5"
                                    onDragging={(isDragging) =>
                                      handleOnDragging(isDragging, row)
                                    }
                                  />
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </PanelGroup>
                    ))}
                  </div>
                </div>
              </div>
              <DragPreview />
            </DragDropProvider>
          )}
      </>
    )
  );
};

const useDashboardDetailsPageQueryStringRedirect = ({
  draftDatabaseRevisionId,
}: {
  draftDatabaseRevisionId: string | null;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setDashboardDatabaseRevisionIdSearchParam = useCallback(
    ({
      newDashboardDatabaseRevisionIdValue,
    }: {
      newDashboardDatabaseRevisionIdValue: string | null;
    }) => {
      const newParams = new URLSearchParams(searchParams);
      if (
        newDashboardDatabaseRevisionIdValue === null &&
        newParams.has('dashboardDatabaseRevisionId')
      ) {
        newParams.delete('dashboardDatabaseRevisionId');
      }

      if (
        newDashboardDatabaseRevisionIdValue !== null &&
        newParams.get('dashboardDatabaseRevisionId') !==
          newDashboardDatabaseRevisionIdValue
      ) {
        newParams.set(
          'dashboardDatabaseRevisionId',
          newDashboardDatabaseRevisionIdValue,
        );
      }

      if (areSearchParamsTheSame(searchParams, newParams)) {
        return;
      }

      setSearchParams((existing) => {
        return areSearchParamsTheSame(existing, newParams)
          ? existing
          : newParams;
      });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    setDashboardDatabaseRevisionIdSearchParam({
      newDashboardDatabaseRevisionIdValue: draftDatabaseRevisionId || null,
    });
  }, [draftDatabaseRevisionId, setDashboardDatabaseRevisionIdSearchParam]);
};
